import { Box, Divider, Typography } from "@mui/material";
import { useCallback, useMemo, useReducer, useRef } from "react";
import EnhancedTable from "components/enhancedTable/EnhancedTable";
import { isLocalDev } from "utils/environment";
import { ContactLensConfigurationService } from "service/validDataService";
import useAbortController from "hooks/useAbortController";
import { DEFAULT_PAGE_SIZE } from "utils";
import useDialogModal from "hooks/useDialogModal";
import ConfirmationDialog from "components/ui/confirmationDialog";
import { toast } from "react-toastify";
import validLookup from "utils/validLookup";
import ContactLensPricingInputForm from "./contactLensPricingInputForm";
import EditContactLensPricingForm from "./editContactLensPricingForm";
import EditIcon from "@mui/icons-material/Edit";
import { makeContactLensPricingColumns } from "./columns";
import { ContentCopy } from "@mui/icons-material";
import useLensTypeCatalog from "hooks/useLensTypeCatalog";

export default function ContactLensPricing() {
    const { contactLensTypeLabels } = useLensTypeCatalog();
    const columns = useMemo(() => makeContactLensPricingColumns(contactLensTypeLabels), [contactLensTypeLabels]);
    const [ContactLensPricingInputDialog, showContactLensPricingInputForm] = useDialogModal(ContactLensPricingInputForm);
    const [ContactLensPricingEditDialog, showContactLensPricingEditForm] = useDialogModal(EditContactLensPricingForm);
    const [
        ConfirmationDialogComp,
        showConfirmationDialog,
        closeConfirmationDialog,
    ] = useDialogModal(ConfirmationDialog);

    const [signal] = useAbortController();
    const lastQueryRef = useRef({ pageSize: DEFAULT_PAGE_SIZE, pageIndex: 0, filters: [], sortBy: [] });
    const requestIdRef = useRef(0);
    const [state, updateState] = useReducer((state, params) => {
        return { ...state, ...params };
    }, {
        data: [],
        totalRowCount: 0,
        pageCount: 0,
        loading: false,
    });

    const onAddRecordClicked = () => {
        showContactLensPricingInputForm();
    }

    const handleRowEdit = (rowData) => {
        showContactLensPricingEditForm(null, { recordToEdit: rowData });
    }

    const handleRowCopy = (rowData) => {
        showContactLensPricingEditForm(null, { recordToEdit: rowData, isCopy: true });
    }

    const fetchData = useCallback(({ pageSize, pageIndex, filters, sortBy }) => {
        const requestId = requestIdRef.current + 1;
        requestIdRef.current = requestId;
        lastQueryRef.current = { pageSize, pageIndex, filters, sortBy };
        const from = pageSize * pageIndex;
        updateState({ loading: true });
        ContactLensConfigurationService.getPricingMatrixWithLimit(from, pageSize, filters, sortBy, signal).then(result => {
            if (requestId !== requestIdRef.current) {
                return;
            }
            const { code, data } = result;
            if (code === 0) {
                updateState({
                    data: data.rows,
                    totalRowCount: data.count,
                    pageCount: Math.ceil(data.count / pageSize),
                });
            }
            updateState({ loading: false });
        }).catch(error => {
            if (requestId !== requestIdRef.current) {
                return;
            }
            if (isLocalDev) {
                console.error(error)
            }
            updateState({ loading: false });
        });
    }, [signal]);

    const deleteRecordHandler = (rowids) => {
        showConfirmationDialog(async (action) => {
            if (action === validLookup.confirmationDialogAction.ok) {
                const deleteRecordResult = await ContactLensConfigurationService.deletePricingMatrix(rowids.map(x => parseInt(x, 10)));
                const { code, message } = deleteRecordResult;

                if (code === 0) {
                    toast.success(message);
                    const updateList = state.data.filter(d => !rowids.map(x => parseInt(x, 10)).includes(d.id));
                    updateState({ data: updateList });
                } else {
                    toast.error("Failed to delete pricing entry(ies)");
                }
            }
            closeConfirmationDialog();
        }, {
            title: 'Delete Pricing Entry',
            message: `Please confirm if you would like to delete ${rowids.length} pricing entr${rowids.length > 1 ? 'ies' : 'y'}?`,
        });
    }

    const recordAddHandler = () => {
        const { pageSize, pageIndex, filters, sortBy } = lastQueryRef.current;
        fetchData({ pageSize, pageIndex, filters, sortBy });
    }

    const recordEditHandler = () => {
        const { pageSize, pageIndex, filters, sortBy } = lastQueryRef.current;
        fetchData({ pageSize, pageIndex, filters, sortBy });
    }

    return (
        <Box>
            {/* <Typography variant="h6">Contact Lens Pricing</Typography> */}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Manage pricing tiers for contact lens models and schedules. Set volume-based pricing by defining box quantity ranges.
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <EnhancedTable
                columns={columns}
                addRecordHandler={onAddRecordClicked}
                deleteRecordHandler={deleteRecordHandler}
                data={state.data}
                totalRowCount={state.totalRowCount}
                pageCount={state.pageCount}
                fetchData={fetchData}
                loading={state.loading}
                selectionMode="multi"
                actions={[
                    {
                        icon: <EditIcon />,
                        onClick: handleRowEdit
                    },
                    {
                        icon: <ContentCopy />,
                        onClick: handleRowCopy
                    },
                ]}
            />
            <ContactLensPricingInputDialog onRecordAdded={recordAddHandler} />
            <ContactLensPricingEditDialog onRecordEdit={recordEditHandler} />
            
            <ConfirmationDialogComp
                label={{
                    positive: "Confirm",
                    negative: "Cancel",
                }} />
        </Box>
    );
}
